import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Twitter } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e8cdf70f-00bc-4528-a3bf-1c9ff1127e96/artifacts/zn03why2_lv_0_20260214074351.png";

const serviceAreas = [
  "Toronto", "North York", "Scarborough", "Etobicoke",
  "Mississauga", "Brampton", "Richmond Hill", "Markham",
  "Vaughan", "Oakville", "Burlington", "Ajax"
];

const services = [
  { name: "Residential Cleaning", path: "/services#residential" },
  { name: "Commercial Cleaning", path: "/services#commercial" },
  { name: "Airbnb Cleaning", path: "/services#airbnb" },
  { name: "Post-Renovation", path: "/services#renovation" },
];

const quickLinks = [
  { name: "About Us", path: "/about" },
  { name: "Our Services", path: "/services" },
  { name: "Pricing", path: "/pricing" },
  { name: "Gallery", path: "/gallery" },
  { name: "Testimonials", path: "/testimonials" },
  { name: "Book Now", path: "/booking" },
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#064E3B] text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <img
              src={LOGO_URL}
              alt="Toronto Premium Cleaning"
              className="h-16 w-auto object-contain mb-6 brightness-0 invert"
            />
            <p className="text-white/80 text-sm leading-relaxed mb-6">
              Premium cleaning services for residential, commercial, Airbnb, and post-renovation properties across the Greater Toronto Area.
            </p>
            <div className="flex gap-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C0A062] transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C0A062] transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C0A062] transition-colors"
                data-testid="social-twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-[#C0A062]">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/80 hover:text-[#C0A062] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-[#C0A062]">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.path}>
                  <Link
                    to={service.path}
                    className="text-white/80 hover:text-[#C0A062] transition-colors text-sm"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-[#C0A062]">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+16478700445"
                  className="flex items-start gap-3 text-white/80 hover:text-[#C0A062] transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">(647) 870-0445</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:torontopclean@gmail.com"
                  className="flex items-start gap-3 text-white/80 hover:text-[#C0A062] transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">torontopclean@gmail.com</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/80">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span className="text-sm">Serving Greater Toronto Area</span>
              </li>
              <li className="flex items-start gap-3 text-white/80">
                <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span className="text-sm">Mon - Sat: 7:00 AM - 8:00 PM<br />Sunday: 9:00 AM - 6:00 PM</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Service Areas */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <h4 className="text-lg font-semibold mb-4 text-[#C0A062]">Service Areas</h4>
          <div className="flex flex-wrap gap-2">
            {serviceAreas.map((area) => (
              <span
                key={area}
                className="px-3 py-1 bg-white/10 rounded-full text-xs text-white/80"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/60">
            <p>© {new Date().getFullYear()} Toronto Premium Cleaning. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="hover:text-[#C0A062] transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-[#C0A062] transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
